import { useCustomerModuleModalRootActionsStore } from '@/features/customers/_shared/components/customer-modal-root/customer-modal-root.store';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  deleteLabelCustomerValidationSchema,
  TDeleteLabelCustomerValidation,
  useDeleteLabelCustomerMutation,
} from '../../../api/delete-label-customer.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_LABEL_CUSTOMER_QUERY_KEY } from '../../../api/get-list-label-customer.api';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
  ModalCloseButton,
} from '@/components/_ui/modal';
import { handleDomBubbleEvent } from '@/utils/dom';
import { Spinner } from '@/components/_ui/spinner';

export type TDeleteLabelCustomerModalType = 'DeleteLabelCustomerModal';
export interface IDeleteLabelCustomerModalProps {
  labelCustomerId: string;
}

const DeleteLabelCustomerModal = memo(({ labelCustomerId }: IDeleteLabelCustomerModalProps) => {
  const customerModuleModalRootActions = useCustomerModuleModalRootActionsStore();
  const showToast = useCustomToast();

  const isOpenDeleteLabelCustomerModal = useMemo(() => {
    return customerModuleModalRootActions.checkIsOpenModal('DeleteLabelCustomerModal');
  }, [customerModuleModalRootActions]);

  const form = useForm<TDeleteLabelCustomerValidation>({
    resolver: zodResolver(deleteLabelCustomerValidationSchema),
    mode: 'onChange',
    defaultValues: {
      labelCustomerId: labelCustomerId || '',
    },
  });

  useEffect(() => {
    if (labelCustomerId) {
      form.reset({
        labelCustomerId: labelCustomerId || '',
      });
    }
  }, [labelCustomerId, form]);

  const mutationDeleteLabelCustomer = useDeleteLabelCustomerMutation({
    mutationConfigOptions: {
      onError: (_error) => {
        const errorMessage = axiosExtractErrorMessage(_error) || 'Error delete label customer, please try again';
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_LABEL_CUSTOMER_QUERY_KEY] })]);
        customerModuleModalRootActions.closeModal('DeleteLabelCustomerModal');
        showToast({
          message: 'Label customer deleted successfully!',
          type: 'success',
        });
      },
    },
  });

  const handleSubmitDeleteLabelCustomer = useCallback(
    (data: TDeleteLabelCustomerValidation) => {
      mutationDeleteLabelCustomer.mutate({
        labelCustomerId: data?.labelCustomerId || '',
      });
    },
    [mutationDeleteLabelCustomer]
  );

  const handleClose = useCallback(() => {
    customerModuleModalRootActions.closeModal('DeleteLabelCustomerModal');
  }, [customerModuleModalRootActions]);

  return (
    <Modal isOpen={isOpenDeleteLabelCustomerModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[500px]">
      <ModalHeader>
        <ModalTitle>Delete Label Customer</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitDeleteLabelCustomer)(e);
          }}
          id="delete-label-customer-form"
          className="grid gap-[16px]"
        >
          <span className="text-[14px] font-normal text-[#9D9D9C]">Do you want to delete this label customer?</span>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton
          form="delete-label-customer-form"
          type="submit"
          disabled={mutationDeleteLabelCustomer.isPending}
        >
          {mutationDeleteLabelCustomer.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null}{' '}
          Delete
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
DeleteLabelCustomerModal.displayName = 'DeleteLabelCustomerModal';
export default DeleteLabelCustomerModal;
